/**
 * POSWorkstationPicker — модал «Оберіть робоче місце».
 *
 * Показуємо коли LocalStorage не має 'pos.workstation_id' (перший вхід)
 * або через hamburger menu «Змінити робоче місце».
 *
 * Список workstations агрегується по всіх accessible orgs касира.
 */
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { orgs } from '@/api/catalogs';
import { retailPosWorkstations } from '@/api/retailPosWorkstations';

interface Props {
  onPick: (workstationId: string) => void;
  onClose?: () => void;
}

export default function POSWorkstationPicker({ onPick, onClose }: Props) {
  const orgList = useQuery({
    queryKey: ['orgs'],
    queryFn: () => orgs.list({ limit: 200 }),
  });

  const [selectedOrg, setSelectedOrg] = useState<string>('');
  useEffect(() => {
    const first = orgList.data?.items[0];
    if (!selectedOrg && first) setSelectedOrg(first.id);
  }, [orgList.data, selectedOrg]);

  const wsQ = useQuery({
    queryKey: ['pos-workstations-for-picker', selectedOrg],
    queryFn: () =>
      retailPosWorkstations.list({
        organization_id: selectedOrg,
        is_active: true,
        limit: 500,
      }),
    enabled: !!selectedOrg,
  });

  const [picked, setPicked] = useState<string>('');

  const activeWorkstations = useMemo(
    () => wsQ.data?.items.filter((w) => w.is_active && !w.deletion_mark) ?? [],
    [wsQ.data],
  );

  return (
    <div className="pos-modal-overlay">
      <div className="pos-modal">
        <div className="pos-modal__header">
          <span>Оберіть робоче місце</span>
          {onClose && (
            <button className="pos-modal__btn-secondary" onClick={onClose}>
              ✕
            </button>
          )}
        </div>
        <div className="pos-modal__body">
          <div className="pos-modal__field">
            <label>Юрособа</label>
            <select
              value={selectedOrg}
              onChange={(e) => {
                setSelectedOrg(e.target.value);
                setPicked('');
              }}
            >
              <option value="">—</option>
              {orgList.data?.items.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.code} — {o.name}
                </option>
              ))}
            </select>
          </div>
          <div className="pos-modal__field">
            <label>POS-станція</label>
            <select value={picked} onChange={(e) => setPicked(e.target.value)}>
              <option value="">— оберіть —</option>
              {activeWorkstations.map((w) => (
                <option key={w.id} value={w.id}>
                  {w.code} — {w.name}
                </option>
              ))}
            </select>
            {activeWorkstations.length === 0 && selectedOrg && (
              <small style={{ color: 'var(--muted)' }}>
                У цій юрособі немає активних POS-станцій. Запитайте адміна.
              </small>
            )}
          </div>
        </div>
        <div className="pos-modal__footer">
          <button
            className="pos-modal__btn-primary"
            disabled={!picked}
            onClick={() => onPick(picked)}
          >
            Обрати
          </button>
        </div>
      </div>
    </div>
  );
}
